import { type SignUpInput, signUpSchema } from '@amore/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GoogleAuthButton } from '@/features/auth/google-auth-button'
import { signUp } from '@/lib/auth'
import { ensureSession, refreshSession } from '@/lib/session'
import { ensureFirstWorkspaceSlug } from '@/lib/workspace'

const searchSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().optional(),
})

function shouldUseDefaultDestination(redirectTo?: string) {
  return !redirectTo || redirectTo === '/workspaces'
}

export const Route = createFileRoute('/signup')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context, search }) => {
    const session = await ensureSession(context.queryClient)

    if (session) {
      if (!shouldUseDefaultDestination(search.redirect)) {
        throw redirect({ to: search.redirect })
      }

      throw redirect(await defaultRedirect(context.queryClient))
    }
  },
  component: SignupPage,
})

async function defaultRedirect(queryClient: QueryClient) {
  const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)

  return workspaceSlug
    ? ({ to: '/w/$workspaceSlug', params: { workspaceSlug } } as const)
    : ({ to: '/onboarding' } as const)
}

function SignupPage() {
  const { redirect: redirectTo, email } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const invitedEmail = email?.trim() ?? ''
  const authSearch = {
    ...(redirectTo ? { redirect: redirectTo } : {}),
    ...(invitedEmail ? { email: invitedEmail } : {}),
  }

  const form = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: '', email: invitedEmail, password: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null)

    const { error } = await signUp.email(values)

    if (error) {
      setFormError(error.message ?? 'Could not create your account.')
      return
    }

    await refreshSession(queryClient)

    if (shouldUseDefaultDestination(redirectTo)) {
      const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)

      if (workspaceSlug) {
        await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug } })
        return
      }

      await navigate({ to: '/onboarding' })
      return
    }

    await navigate({ to: redirectTo })
  })

  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="page-stagger-root w-full max-w-sm">
        <h1 className="text-2xl font-semibold tracking-tight">Create your account</h1>

        <div className="mt-8">
          <GoogleAuthButton
            redirectTo={redirectTo}
            email={invitedEmail}
            mode="signup"
            onError={setFormError}
          />
        </div>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          <span>or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium">
              Name
            </label>
            <Input
              id="name"
              autoComplete="name"
              aria-invalid={Boolean(form.formState.errors.name)}
              {...form.register('name')}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              readOnly={Boolean(invitedEmail)}
              aria-invalid={Boolean(form.formState.errors.email)}
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
            {form.formState.errors.password && (
              <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
            )}
          </div>

          {formError && (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        <p className="mt-6 text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link
            to="/login"
            search={authSearch}
            className="font-medium text-foreground underline underline-offset-4"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
